import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getSupervisorLogs, deleteSupervisorLog } from "../../services/supervisorService";
import { format } from 'date-fns';
import {
    AiOutlineCalendar,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineEye,
    AiOutlineFilter,
    AiOutlineReload
} from "react-icons/ai";
import LogDetailsModal from "./LogDetailsModal";
import DailyLogForm from "./DailyLogForm";
import Popup from "../../components/ui/Popup";

const DailyLogsList = ({ showPopup }) => {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [dateRange, setDateRange] = useState({
        start_date: "",
        end_date: ""
    });
    const [selectedLog, setSelectedLog] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [logToDelete, setLogToDelete] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const logsPerPage = 8;

    useEffect(() => {
        fetchLogs();
    }, [currentPage]);

    useEffect(() => {
        filterLogs();
    }, [logs, searchQuery, dateRange]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: logsPerPage
            };

            if (dateRange.start_date) params.start_date = dateRange.start_date;
            if (dateRange.end_date) params.end_date = dateRange.end_date;

            console.log("Fetching logs with params:", params);
            const response = await getSupervisorLogs(params);
            console.log("Logs API response:", response);

            if (response && response.success) {
                setLogs(response.data);
                setTotalPages(response.pagination.total_pages);
                console.log("Logs loaded successfully:", response.data);
            } else {
                console.error("Failed to load logs, API returned:", response);
                showPopup("Failed to load supervisor logs", "error");
                setLogs([]);
            }
        } catch (error) {
            console.error("Error fetching logs:", error);
            showPopup("Failed to load supervisor logs", "error");
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const filterLogs = () => {
        let filtered = [...logs];

        // Apply search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(log =>
                log.notes?.toLowerCase().includes(query) ||
                log.shift?.toLowerCase().includes(query) ||
                log.supervisor_name?.toLowerCase().includes(query)
            );
        }

        setFilteredLogs(filtered);
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleFilter = () => {
        setCurrentPage(1);
        fetchLogs();
    };

    const resetFilters = () => {
        setDateRange({
            start_date: "",
            end_date: ""
        });
        setSearchQuery("");
        setCurrentPage(1);
        fetchLogs();
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const viewLogDetails = (log) => {
        setSelectedLog(log);
        setIsDetailsModalOpen(true);
    };

    const editLog = (log) => {
        setSelectedLog(log);
        setIsEditModalOpen(true);
    };

    const confirmDelete = (log) => {
        setLogToDelete(log);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        if (!logToDelete) return;

        try {
            const response = await deleteSupervisorLog(logToDelete.id);

            if (response && response.success) {
                showPopup("Log deleted successfully", "success");
                fetchLogs();
            } else {
                showPopup("Failed to delete log", "error");
            }
        } catch (error) {
            console.error("Error deleting log:", error);
            showPopup("Failed to delete log", "error");
        } finally {
            setShowDeleteConfirm(false);
            setLogToDelete(null);
        }
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch (e) {
            return dateString;
        }
    };

    const getShiftBadgeClass = (shift) => {
        switch (shift?.toLowerCase()) {
            case 'morning': return 'morning';
            case 'evening': return 'evening';
            case 'night': return 'night';
            default: return '';
        }
    };

    return (
        <Container>
            <Header>
                <h2>Daily Work Logs</h2>
                <RefreshButton onClick={fetchLogs}>
                    <AiOutlineReload />
                </RefreshButton>
            </Header>

            <FilterSection>
                <DateFilters>
                    <FilterGroup>
                        <FilterLabel>
                            <AiOutlineCalendar />
                            <span>Start Date:</span>
                        </FilterLabel>
                        <FilterInput
                            type="date"
                            name="start_date"
                            value={dateRange.start_date}
                            onChange={handleDateChange}
                        />
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>
                            <AiOutlineCalendar />
                            <span>End Date:</span>
                        </FilterLabel>
                        <FilterInput
                            type="date"
                            name="end_date"
                            value={dateRange.end_date}
                            onChange={handleDateChange}
                        />
                    </FilterGroup>

                    <FilterButtons>
                        <ApplyButton onClick={handleFilter}>
                            <AiOutlineFilter />
                            <span>Apply Filters</span>
                        </ApplyButton>

                        <ResetButton onClick={resetFilters}>
                            Reset
                        </ResetButton>
                    </FilterButtons>
                </DateFilters>

                <SearchBox>
                    <SearchIcon>
                        <AiOutlineSearch />
                    </SearchIcon>
                    <SearchInput
                        type="text"
                        placeholder="Search logs..."
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                </SearchBox>
            </FilterSection>

            {loading ? (
                <LoadingState>Loading logs...</LoadingState>
            ) : (
                <>
                    {filteredLogs.length > 0 ? (
                        <LogsTable>
                            <thead>
                            <tr>
                                <th>Date</th>
                                <th>Shift</th>
                                <th>Activities</th>
                                <th>Notes</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredLogs.map((log) => (
                                <tr key={log.id}>
                                    <td>{formatDate(log.log_date)}</td>
                                    <td>
                                        <ShiftBadge className={getShiftBadgeClass(log.shift)}>
                                            {log.shift}
                                        </ShiftBadge>
                                    </td>
                                    <td>
                                        {log.activities?.length || 0} activities
                                    </td>
                                    <td>
                                        <NotesTruncated>
                                            {log.notes || "No notes provided"}
                                        </NotesTruncated>
                                    </td>
                                    <td>
                                        <ActionButtons>
                                            <ActionButton title="View Details" onClick={() => viewLogDetails(log)}>
                                                <AiOutlineEye />
                                            </ActionButton>
                                            <ActionButton title="Edit Log" onClick={() => editLog(log)}>
                                                <AiOutlineEdit />
                                            </ActionButton>
                                            <ActionButton className="delete" title="Delete Log" onClick={() => confirmDelete(log)}>
                                                <AiOutlineDelete />
                                            </ActionButton>
                                        </ActionButtons>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </LogsTable>
                    ) : (
                        <EmptyState>
                            <p>No logs found</p>
                            <small>Try adjusting your search or filter criteria</small>
                        </EmptyState>
                    )}

                    {totalPages > 1 && (
                        <Pagination>
                            <PaginationButton
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </PaginationButton>

                            <PageInfo>
                                Page {currentPage} of {totalPages}
                            </PageInfo>

                            <PaginationButton
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </PaginationButton>
                        </Pagination>
                    )}
                </>
            )}

            {/* View Details Modal */}
            {isDetailsModalOpen && selectedLog && (
                <LogDetailsModal
                    log={selectedLog}
                    onClose={() => setIsDetailsModalOpen(false)}
                />
            )}

            {/* Edit Log Modal */}
            {isEditModalOpen && selectedLog && (
                <EditModalWrapper>
                    <EditModalContent>
                        <EditModalHeader>
                            <h3>Edit Daily Log</h3>
                            <CloseButton onClick={() => setIsEditModalOpen(false)}>×</CloseButton>
                        </EditModalHeader>

                        <DailyLogForm
                            selectedLog={selectedLog}
                            showPopup={showPopup}
                            onSuccess={() => {
                                setIsEditModalOpen(false);
                                fetchLogs();
                            }}
                        />
                    </EditModalContent>
                </EditModalWrapper>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <ConfirmDialog>
                    <ConfirmContent>
                        <h3>Confirm Deletion</h3>
                        <p>Are you sure you want to delete this log from {formatDate(logToDelete?.log_date)}?</p>
                        <p>This action cannot be undone.</p>

                        <ConfirmActions>
                            <CancelButton onClick={() => setShowDeleteConfirm(false)}>
                                Cancel
                            </CancelButton>
                            <DeleteButton onClick={handleDelete}>
                                Delete
                            </DeleteButton>
                        </ConfirmActions>
                    </ConfirmContent>
                </ConfirmDialog>
            )}
        </Container>
    );
};

export default DailyLogsList;

// Styled Components
const Container = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 {
        font-size: 18px;
        margin: 0;
        color: #333;
    }
`;

const RefreshButton = styled.button`
    background: none;
    border: none;
    color: #777;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #000;
    }
`;

const FilterSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
    
    @media (min-width: 768px) {
        flex-direction: row;
        justify-content: space-between;
    }
`;

const DateFilters = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const FilterLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: #555;
    
    svg {
        font-size: 14px;
    }
`;

const FilterInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const FilterButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const ApplyButton = styled.button`
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: #000;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover {
        background-color: #333;
    }
    
    svg {
        font-size: 14px;
    }
`;

const ResetButton = styled.button`
    background-color: #f5f5f5;
    color: #666;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover {
        background-color: #e0e0e0;
    }
`;

const SearchBox = styled.div`
    position: relative;
    flex-grow: 1;
    max-width: 300px;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #777;
    font-size: 16px;
    display: flex;
    align-items: center;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 8px 12px 8px 35px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 30px;
    color: #777;
`;

const LogsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    
    th, td {
        padding: 12px 15px;
        text-align: left;
        border-bottom: 1px solid #eee;
    }
    
    th {
        background-color: #f9f9f9;
        font-weight: 600;
        font-size: 14px;
        color: #333;
    }
    
    td {
        font-size: 14px;
        color: #444;
    }
    
    tr:hover {
        background-color: #f5f5f5;
    }
`;

const ShiftBadge = styled.span`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    
    &.morning {
        background-color: #e3f2fd;
        color: #1976d2;
    }
    
    &.evening {
        background-color: #fff8e1;
        color: #ff8f00;
    }
    
    &.night {
        background-color: #ede7f6;
        color: #5e35b1;
    }
`;

const NotesTruncated = styled.div`
    max-width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #666;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 5px;
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #555;
    cursor: pointer;
    
    &:hover {
        background-color: #f0f0f0;
        color: #000;
    }
    
    &.delete {
        &:hover {
            background-color: #ffebee;
            color: #f44336;
        }
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #777;
    
    p {
        font-size: 16px;
        margin-bottom: 5px;
    }
    
    small {
        font-size: 14px;
        color: #999;
    }
`;

const Pagination = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    gap: 10px;
`;

const PaginationButton = styled.button`
    padding: 6px 12px;
    border: 1px solid #ddd;
    background-color: white;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover:not(:disabled) {
        background-color: #f5f5f5;
    }
    
    &:disabled {
        color: #ccc;
        cursor: not-allowed;
    }
`;

const PageInfo = styled.div`
    font-size: 14px;
    color: #666;
`;

const EditModalWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const EditModalContent = styled.div`
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 20px;
`;

const EditModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h3 {
        font-size: 18px;
        margin: 0;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    color: #777;
    cursor: pointer;
    
    &:hover {
        color: #000;
    }
`;

const ConfirmDialog = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ConfirmContent = styled.div`
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 400px;
    padding: 20px;
    
    h3 {
        font-size: 18px;
        margin: 0 0 15px 0;
        color: #f44336;
    }
    
    p {
        margin: 5px 0;
        font-size: 14px;
        color: #555;
    }
`;

const ConfirmActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
`;

const CancelButton = styled.button`
    background-color: #f5f5f5;
    color: #333;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover {
        background-color: #e0e0e0;
    }
`;

const DeleteButton = styled.button`
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover {
        background-color: #d32f2f;
    }
`;